import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Home() {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="abstract-shape shape-1"></div>
          <div className="abstract-shape shape-2"></div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">Structurer pour fiabiliser</h1>
          <p className="hero-subtitle">
            Open Cortex accompagne les organisations dans la clarification, la structuration et la fiabilisation de leurs processus opérationnels.
          </p>
          <div className="hero-actions">
            <Link to="/contact">
              <Button className="btn-primary">
                Prendre RDV <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/offre">
              <Button variant="outline" className="btn-secondary">
                Découvrir notre offre
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">Notre Mission</span>
            <h2 className="section-title">Ce que fait Open Cortex</h2>
          </div>
          <div className="mission-content">
            <p className="lead-text">
              Nous intervenons en amont des choix d'outils et de technologies. Notre rôle est de comprendre ce qui compte vraiment dans votre fonctionnement, de le structurer avec rigueur, puis de déterminer comment l'automatisation et l'intelligence artificielle peuvent servir vos besoins réels.
            </p>
            <p className="lead-text-secondary">
              Notre intervention se situe en amont des décisions techniques : nous analysons votre fonctionnement actuel, formalisons vos processus et concevons des solutions adaptées à votre maturité organisationnelle et à vos contraintes opérationnelles. Nous travaillons à partir de ce qui existe, pour le consolider et le rendre transmissible.
            </p>
            <div className="mission-grid">
              <div className="mission-card">
                <h3>Clarifier</h3>
                <p>Identifier ce qui structure réellement votre activité, au-delà des apparences.</p>
              </div>
              <div className="mission-card">
                <h3>Structurer</h3>
                <p>Formaliser les processus de manière rigoureuse et adaptée à votre contexte.</p>
              </div>
              <div className="mission-card">
                <h3>Consolider</h3>
                <p>Renforcer la stabilité de vos opérations, les rendre traçables et transmissibles dans la durée.</p>
              </div>
              <div className="mission-card">
                <h3>Automatiser avec discernement</h3>
                <p>Utiliser l'automatisation et l'IA uniquement là où elles ont du sens.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Context Section */}
      <section className="context-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">Contexte d'intervention</span>
            <h2 className="section-title">Quand Open Cortex intervient</h2>
          </div>
          <div className="context-content">
            <div className="context-list">
              <div className="context-item">
                <CheckCircle2 className="context-icon" />
                <div>
                  <h4>Certaines choses reposent trop sur vous</h4>
                  <p>Votre organisation tourne, vous savez comment elle fonctionne. Mais vous sentez qu'une part importante de ce savoir reste dans votre tête, et vous aimeriez pouvoir le transmettre plus facilement, déléguer avec plus de confiance.</p>
                </div>
              </div>
              <div className="context-item">
                <CheckCircle2 className="context-icon" />
                <div>
                  <h4>Vous passez du temps sur des tâches répétitives</h4>
                  <p>Vous voyez bien où l'automatisation pourrait vous soulager. Mais avant d'engager un prestataire ou de choisir un outil, vous préférez clarifier ce qui doit vraiment être automatisé, et dans quel ordre.</p>
                </div>
              </div>
              <div className="context-item">
                <CheckCircle2 className="context-icon" />
                <div>
                  <h4>Vous préparez une transition</h4>
                  <p>Transmission, conformité, croissance : vous savez que formaliser ce qui fonctionne aujourd'hui vous facilitera la suite. Vous cherchez une méthode pour y arriver sans tout chambouler.</p>
                </div>
              </div>
              <div className="context-item">
                <CheckCircle2 className="context-icon" />
                <div>
                  <h4>Vous décidez beaucoup, souvent dans l'urgence</h4>
                  <p>Votre expérience vous porte, mais vous ressentez la fatigue de trancher sans bases solides. Vous aimeriez disposer de repères plus clairs pour alléger cette charge mentale.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Role Section */}
      <section className="role-section">
        <div className="section-container">
          <div className="section-header centered">
            <h2 className="section-title">Le rôle d'Open Cortex</h2>
            <p className="section-description">
              Nous agissons comme conseil opérationnel et architecte organisationnel. Notre intervention se situe en amont des décisions techniques et vise à poser un cadre structuré et transmissible. Nous travaillons à partir de vos méthodes actuelles pour les formaliser, en renforcer la cohérence et faciliter leur continuité. Cette consolidation vous permet des arbitrages plus éclairés et une allocation plus fluide de vos ressources.
            </p>
          </div>
          <div className="role-grid">
            <div className="role-card">
              <div className="role-number">01</div>
              <h3>Observer et comprendre</h3>
              <p>Nous observons votre fonctionnement tel qu'il se déroule au quotidien, dans ses détails opérationnels et ses interactions concrètes.</p>
            </div>
            <div className="role-card">
              <div className="role-number">02</div>
              <h3>Structurer et formaliser</h3>
              <p>Nous documentons vos processus de manière rigoureuse, adaptée et utilisable au quotidien.</p>
            </div>
            <div className="role-card">
              <div className="role-number">03</div>
              <h3>Recommander et accompagner</h3>
              <p>Nous proposons des solutions pragmatiques, issues du terrain, et nous vous transférons les compétences nécessaires.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Example Section */}
      <section className="example-section">
        <div className="section-container">
          <div className="section-intro">
            <span className="section-label">Situations d'intervention</span>
            <p className="section-intro-text">
              Voici quelques situations typiques dans lesquelles Open Cortex intervient, parmi d'autres.
            </p>
          </div>
          
          <div className="examples-grid">
            <div className="example-content">
              <h3>Qualification et suivi des leads</h3>
              <div className="example-details">
                <h4>Contexte</h4>
                <p>Volume de prospects élevé, suivi chronophage, risque de perte d'information entre les étapes.</p>
                <h4>Intervention</h4>
                <p>Structuration du flux de qualification, automatisation ciblée des relances et de la priorisation, outillage adapté à la taille de l'équipe.</p>
                <h4>Effet recherché</h4>
                <p>Temps libéré pour l'équipe commerciale, meilleure attention portée aux prospects à fort potentiel, décisions plus fluides sur l'allocation des efforts.</p>
              </div>
            </div>

            <div className="example-content">
              <h3>Assistant vocal pour établissement recevant des appels en continu</h3>
              <div className="example-details">
                <h4>Contexte</h4>
                <p>Appels fréquents en période de rush ou hors horaires d'ouverture, saturation ponctuelle, difficulté à prioriser les urgences.</p>
                <h4>Intervention</h4>
                <p>Mise en place d'un assistant vocal capable de répondre aux demandes courantes, qualifier les besoins et orienter vers le bon interlocuteur.</p>
                <h4>Effet recherché</h4>
                <p>Continuité de service améliorée, meilleure gestion des priorités, fluidité opérationnelle renforcée sans augmentation des effectifs.</p>
              </div>
            </div>

            <div className="example-content">
              <h3>Structuration et transmission des processus internes</h3>
              <div className="example-details">
                <h4>Contexte</h4>
                <p>Fonctionnement reposant sur quelques personnes clés, documentation existante mais hétérogène, difficulté à former les nouveaux arrivants.</p>
                <h4>Intervention</h4>
                <p>Clarification des processus critiques, formalisation dans un format utilisable au quotidien, outillage adapté pour faciliter la mise à jour.</p>
                <h4>Effet recherché</h4>
                <p>Continuité assurée en cas d'absence, meilleure lisibilité pour l'équipe, décisions plus sereines sur les évolutions organisationnelles.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Nous contacter</h2>
            <p>Pour aborder un sujet, une question ou une situation concrète.</p>
            <Link to="/contact">
              <Button className="btn-primary btn-large">
                Prendre contact <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}